import React, { FC, useState } from 'react';

import { mqMin } from '@lucid/theme';

import { LucidTheme, Component } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Link from '../link/link';
import Modal from '../modal/modal';
import Text from '../text/text';
import { ComponentLink, ComponentImage } from '../../types/generics';

export interface HeroProps {
  /** Allows the image to be clickable to open in a modal. */
  canClickImage?: boolean;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The button CTA link and text. */
  cta?: ComponentLink;
  /** show the cta as a link instead of a button */
  willDisplayCtaAsLink?: boolean;
  /** Adjusts text color for dark backgrounds */
  hasDarkBackground?: boolean;
  /** The heading text. */
  header: string;
  /** The header variants. */
  headerVariant?: Component.Type['Heading'];
  /** An image to be placed next to the text. */
  image?: ComponentImage;
  /** Checks if the Hero image should overflow with increased size. */
  shouldImageOverflow?: boolean;
  /** Checks if the Hero image should stack on smaller screens. */
  shouldImageStack?: boolean;
  /** The subheader text displayed under the heading text. */
  subheader: string;
  /** The subheader text displayed under the heading text. */
  subheaderVariant?: Component.Type['Typography'];
}

const Hero: FC<HeroProps> = ({
  canClickImage = false,
  className,
  cta,
  willDisplayCtaAsLink,
  hasDarkBackground,
  header,
  headerVariant = 'headline3XL',
  image,
  shouldImageOverflow = false,
  shouldImageStack = false,
  subheader,
  subheaderVariant = 'paragraphM400',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasImage = image && image.imageSrc;

  const handleOnClick = (e: React.SyntheticEvent<Element, Event>) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const renderImage = (image?: ComponentImage, isImageInModal = false) => (
    <Box
      css={(theme: LucidTheme) => [
        {
          display: 'none',
          visibility: 'hidden',
          width: '100%',
          maxWidth: '580px',
          marginTop: theme.space.s40,
          [mqMin.tabletSmall]: {
            display: 'block',
            visibility: 'visible',
            marginTop: 0,
          },
        },
        shouldImageStack && {
          [mqMin.tabletSmall]: {
            display: 'block',
            visibility: 'visible',
          },
          [mqMin.tabletSmall]: {
            marginTop: theme.space.s40,
          },
          [mqMin.tabletLandscape]: {
            marginTop: 0,
          },
        },
        isImageInModal && {
          [mqMin.tablet]: {
            width: '750px',
          },
        },
      ]}
    >
      <ImageWithPlaceholder
        alt={image?.imageAlt || ''}
        aspectRatio="4:3"
        css={[
          shouldImageOverflow && {
            overflow: 'unset',
            width: '150%',
            '& .image': {
              height: '200%',
              width: '200%',
              top: '50%',
              left: '-50%',
              zIndex: -1,
            },
          },
          isImageInModal && {
            [mqMin.tablet]: {
              '& .image': {
                maxWidth: '750px',
                width: '100%',
              },
            },
          },
        ]}
        dataElementPageLocation="hero"
        src={image?.imageSrc || ''}
      />
    </Box>
  );

  return (
    <Box
      css={(theme: LucidTheme) => [
        {
          padding: `${theme.space.s64}px 0 ${theme.space.s16}px`,
          [mqMin.tablet]: {
            padding: `${theme.space.s64}px 0`,
          },
        },
        hasImage && {
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          [mqMin.tabletSmall]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
          },
        },
        hasImage &&
          shouldImageStack && {
            [mqMin.tabletSmall]: {
              flexDirection: 'column',
            },
            [mqMin.tabletLandscape]: {
              flexDirection: 'row',
            },
          },
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      <Box
        css={(theme: LucidTheme) => [
          {
            paddingRight: 0,
          },
          hasImage && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            [mqMin.tabletSmall]: {
              maxWidth: '440px',
              paddingRight: theme.space.s40,
              alignItems: 'start',
            },
          },
          hasImage &&
            shouldImageStack && {
              [mqMin.tabletSmall]: {
                alignItems: 'center',
              },
              [mqMin.tabletLandscape]: {
                alignItems: 'start',
              },
            },
        ]}
      >
        <Heading
          css={(theme: LucidTheme) => [
            {
              textAlign: 'center',
              width: '100%',
            },
            hasImage && {
              [mqMin.tabletSmall]: {
                textAlign: 'left',
              },
            },
            hasImage &&
              shouldImageStack && {
                [mqMin.tabletSmall]: {
                  textAlign: 'center',
                },
                [mqMin.tabletLandscape]: {
                  textAlign: 'left',
                },
              },
            hasDarkBackground && {
              color: theme.colors.white,
            },
          ]}
          dataElementPageLocation="hero"
          variant={headerVariant}
        >
          {header}
        </Heading>
        <Text
          css={(theme: LucidTheme) => [
            {
              width: '100%',
              textAlign: 'center',
            },
            hasImage && {
              [mqMin.tabletSmall]: {
                textAlign: 'left',
              },
            },
            hasImage &&
              shouldImageStack && {
                [mqMin.tabletSmall]: {
                  textAlign: 'center',
                },
                [mqMin.tabletLandscape]: {
                  textAlign: 'left',
                },
              },
            hasDarkBackground && {
              color: theme.colors.white,
            },
          ]}
          dataElementPageLocation="hero"
          variant={subheaderVariant}
        >
          {subheader}
        </Text>
        <Box
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              [mqMin.tabletSmall]: {
                width: 'initial',
              },
            },
          ]}
        >
          {cta && cta.linkUrl && (
            <Link displayAsButton={!willDisplayCtaAsLink} hasArrow={willDisplayCtaAsLink} href={cta.linkUrl}>
              {cta.linkText}
            </Link>
          )}
        </Box>
      </Box>
      {hasImage &&
        (canClickImage ? (
          <Link
            css={{
              width: '100%',
              display: 'none',
              visibility: 'hidden',
              [mqMin.tabletSmall]: {
                display: 'block',
              },
            }}
            onClick={handleOnClick}
          >
            {renderImage(image)}
          </Link>
        ) : (
          renderImage(image)
        ))}
      {canClickImage && (
        <Modal
          css={(theme: LucidTheme) => ({
            width: '100%',
            padding: theme.space.s64,
            zIndex: theme.zIndices.z1000,
            display: 'none',
            visibility: 'hidden',
            [mqMin.tabletSmall]: {
              display: 'block',
            },
          })}
          isOpen={isOpen}
          onRequestClose={handleOnClick}
        >
          {renderImage(image, true)}
        </Modal>
      )}
    </Box>
  );
};

export { Hero };
export default Hero;
